import { BookData } from '../interfaces/book-data.interface';
import { CollageData } from '../interfaces/collage-data.interface';
import { DrawableData, isDrawableGroup, isDrawableText } from '../interfaces/drawable-data.interface';

/**
 * One visible text box that prints blank. Page numbering matches every other refusal surface:
 * cover 0, content spreads 1…N (the editor strip and the `spread` route parameter).
 */
export interface EmptyTextFieldRef {
  readonly pageIndex: number;
  readonly textType?: string;
  readonly id: string;
}

/**
 * THE structural walk behind "a visible text field is empty" — the text twin of
 * `findEmptyPhotoFrames`. It judges exactly the model it is given, so the CALLER owns the print
 * contract: person-typed texts (name, quote) are blank in the PERSISTED model by design and get
 * their value from read-time substitution — feed this walk the substituted model
 * (`applySettingsToModel` → `applyPersonsToVignetteModel` in strict-blanks mode), never the raw
 * one, or it will refuse albums that print perfectly. `findEmptyPrintedTextFields`
 * (dto-fotoclass-book) is that wrapper; gates go through it.
 *
 * A hidden or non-rendered box never reaches paper, so it cannot print a hole — skipped before the
 * group recursion because a hidden group hides its whole subtree (the walk both renderers do).
 * Whitespace-only counts as empty, same as the cover gate and the editor hint.
 *
 * A missing book is NOT "no empty texts": callers gate on a model they could actually read and must
 * treat `null` as their own fail-closed case rather than reading an empty list out of this function.
 */
export function findEmptyTextFields(book: Pick<BookData, 'cover' | 'spreads'>): readonly EmptyTextFieldRef[] {
  const found: EmptyTextFieldRef[] = [];

  const coverPresent = Boolean(book.cover);
  if (coverPresent) collectInCollages(book.cover?.collages, 0, found);

  const offset = coverPresent ? 1 : 0;
  (book.spreads ?? []).forEach((spread, index) => collectInCollages(spread?.collages, offset + index, found));

  return found;
}

function collectInCollages(collages: readonly CollageData[] | undefined, pageIndex: number, sink: EmptyTextFieldRef[]): void {
  for (const collage of collages ?? []) collectInDrawables(collage.drawableObjects, pageIndex, sink);
}

function collectInDrawables(drawables: readonly DrawableData[] | undefined, pageIndex: number, sink: EmptyTextFieldRef[]): void {
  for (const drawable of drawables ?? []) {
    if (drawable.visible === false || drawable.skipRender === true) continue;

    if (isDrawableGroup(drawable)) {
      collectInDrawables(drawable.drawableObjects, pageIndex, sink);
      continue;
    }

    if (!isDrawableText(drawable)) continue;
    if ((drawable.text?.text ?? '').trim() !== '') continue;

    sink.push({ pageIndex, textType: drawable.textType, id: drawable.id });
  }
}
